<template>
  <div
    v-if="data.valuateOnly"
    class="bg-surface-primary rounded-md border-1 border-border-tertiary"
  >
    <div class="p-24">
      <p v-if="valuateOnlyHeading" class="heading-xs pb-8">
        {{ valuateOnlyHeading }}
      </p>
      <p v-if="valuateOnlyIngress" class="heading-xxs pb-8">
        {{ valuateOnlyIngress }}
      </p>
    </div>
    <div class="flex flex-col">
      <BuybackValuateOnlyStep
        v-for="(step, index) in valuateOnlySteps"
        :key="index"
        v-model="showDrawer"
        :step="(index + 1).toString()"
        :heading="step.heading"
        :text="step.text"
        :active="index == 0 ? true : false"
        :button-text="index == 0 ? 'Värdera' : ''"
      />
    </div>
    <p v-if="valuateOnlyText" class="bg-surface-modal px-24 pb-24 pt-8">
      {{ valuateOnlyText }}
    </p>
  </div>
  <div
    v-else-if="data.type == 'toOrder'"
    class="flex flex-col-reverse md:flex-row md:items-start"
  >
    <div class="w-full md:w-1/2">
      <div class="mt-16 mb-8 md:mt-0">
        <BoxedText
          box-color-class="bg-surface-secondary"
          element="h2"
          text-class="heading-xxs"
          text-color-class="text-text-on-highlight"
        >
          Dumpa din lur
        </BoxedText>
        <div class="py-4"></div>
        <BoxedText
          box-color-class="bg-surface-information"
          text-color-class="text-text-primary-inverted"
          element="p"
          text-class="heading-sm"
        >
          och sänk kostnaden!
        </BoxedText>
      </div>
      <div class="mt-16 mb-16 md:mt-26 md:mb-24 max-w-600">
        <p class="heading-xxl">
          Hos oss kan du dumpa din gamla mobil och få en bättre deal. Välj
          mellan lägre månadskostnad eller mer surf varje månad.
        </p>
        <p>
          Din värdering och deal beror på telefonmodell och skick. Dealen som
          visas upp efter din värdering är ett uppskattningsvärde som
          slutvärderas när vi mottagit din gamla mobil.
        </p>
      </div>
      <AppButton color="secondary" @on-click="showDrawer = true"
        >Värdera din mobil</AppButton
      >
    </div>
    <div class="w-full md:w-1/2">
      <NuxtImg
        loading="lazy"
        class="w-full rounded-t-sm md:rounded-t-0 md:rounded-tr-sm md:rounded-br-sm"
        src="/images/product_related/broken-phone.png"
        alt=""
        provider="cloudfront"
      />
    </div>
  </div>
  <div v-else class="bg-surface-primary">
    <div
      :class="[
        'h-[6.1rem]',
        `bg-[url('/public/shop/images/checkout/Buyback/buyback-tejp-mobile.svg')]`,
        `sm:bg-[url('/public/shop/images/checkout/Buyback/buyback-tejp-bred-mobil.svg')]`,
        `md:bg-[url('/public/shop/images/checkout/Buyback/buyback-tejp-desktop.svg')]`,
        'bg-no-repeat',
        'bg-cover',
      ]"
    ></div>
    <div class="w-full flex flex-col items-center gap-16 px-16 pb-16">
      <h2 class="heading-xs">Dumpa din lur</h2>
      <div
        v-if="buybackStore.campaignHero"
        v-html="buybackStore.campaignHero"
      ></div>
      <p v-else>
        Ge oss din gamla lur och få mer gigg eller sänkt månadskostnad
      </p>
      <AppButton
        color="secondary"
        @on-click="((showDrawer = true), buybackStore.reset())"
        >Kolla värdet</AppButton
      >
    </div>
  </div>

  <AppDrawer
    v-model="showDrawer"
    dialog-titel="Lägg till en begagnad telefon"
    @on-scroll="buybackStore.showScrollIndicator = false"
  >
    <div class="mx-[-2.4rem] mt-[-3.2rem] p-24 pt-32">
      <h2 class="heading-xs pb-8">Dumpa din lur</h2>
      <p>Ge oss din gamla lur och få mer gig eller sänkt månadskostnad.</p>
    </div>

    <BuybackStep icon="search" heading="Hitta din modell" :active="true">
      <div ref="target" class="pt-24" tabindex="0">
        <InputText
          v-model="buybackStore.selectedBuybackName"
          width="w-full md:w-full"
          name="buyback"
          type="text"
          label="Din telefonmodell"
          help-text="Till exempel iphone 12"
          placeholder="Din telefonmodell"
          :input-debounce-ms="400"
          @input="
            (val: any) => buybackStore.buybackSearch(val.target.value.trim())
          "
          @click="buybackStore.searchDropdownVisible = true"
        >
          <template #selectionContainer>
            <div>
              <ul
                v-if="
                  buybackStore.searchResults.length &&
                  buybackStore.searchDropdownVisible
                "
                class="absolute bg-surface-primary w-full rounded-md shadow-card p-16 max-h-[27.8rem] overflow-y-auto"
                tabindex="0"
              >
                <li
                  v-for="item in buybackStore.searchResults"
                  :key="item?.BuybackItemId"
                  class="px-8 py-12 cursor-pointer rounded-md hover:bg-surface-action-hover"
                  role="button"
                  tabindex="0"
                  @click="buybackStore.selectBuyback(item)"
                  @keyup.enter.self.prevent="buybackStore.selectBuyback(item)"
                >
                  <p>
                    {{ item?.Manufacturer }}
                    <span class="heading-xxs">{{ item?.ModelName }}</span>
                    {{ item?.Storage }}
                    {{ item?.Color }}
                  </p>
                </li>
              </ul>
            </div>
          </template>
        </InputText>
      </div>
    </BuybackStep>
    <BuybackStep
      icon="recycle-diamond"
      heading="Välj skick"
      :active="buybackStore.step2Active"
    >
      <div v-if="buybackStore.step2Active">
        <div class="pt-16">
          <InputRadioGroup>
            <BuybackCondition
              v-model="buybackStore.selectedCondition"
              :value="PERFECT_CONDITION"
              label="Perfekt skick"
              group-name="select_buyback_condition"
              @update:model-value="buybackStore.changeCondition()"
            >
              <p class="pt-16">
                Mobilen är fullt fungerande och går att ladda. Skärmen lyser upp
                utan döda pixlar. Är helt fri från repor, skador eller sprickor
                på skärmen/baksidan och är inte kantstött.
                <span
                  v-if="
                    buybackStore.selectedBuybackProduct?.Manufacturer ===
                    'Apple'
                  "
                  >Batteri över 90%.</span
                >
              </p>

              <p class="heading-xxs">
                Upp till
                {{ buybackStore.conditionPerfect?.TradeInMonthlyValue }}
                kr/mån eller
                {{ buybackStore.conditionPerfect?.TradeInMonthlyDataAmount }} GB
              </p>
            </BuybackCondition>
            <BuybackCondition
              v-model="buybackStore.selectedCondition"
              :value="GOOD_CONDITION"
              label="Bra skick"
              group-name="select_buyback_condition"
              @update:model-value="buybackStore.changeCondition()"
            >
              <p class="pt-16">
                Mobilen fungerar som den ska och går att ladda. Skärmen lyser
                upp utan döda pixlar. Har synliga tecken på slitage, såsom
                mindre repor, andra märken på skärm/baksidan och eller
                kantstött.
                <span
                  v-if="
                    buybackStore.selectedBuybackProduct?.Manufacturer ===
                    'Apple'
                  "
                  >Batteri över 80%.</span
                >
              </p>
              <p class="heading-xxs">
                Upp till
                {{ buybackStore.conditionGood?.TradeInMonthlyValue }}
                kr/mån eller
                {{ buybackStore.conditionGood?.TradeInMonthlyDataAmount }} GB
              </p>
            </BuybackCondition>
            <BuybackCondition
              v-model="buybackStore.selectedCondition"
              :value="DAMAGED_CONDITION"
              label="Skadat skick"
              group-name="select_buyback_condition"
              @update:model-value="buybackStore.changeCondition()"
            >
              <p class="pt-16">
                Mobilen fungerar eller är i skadat skick. Har tydliga tecken på
                slitage, såsom djupa repor, bucklor och märken på skärm/baksidan
                och kanter.
              </p>
              <p class="heading-xxs">
                Upp till
                {{ buybackStore.conditionDamaged?.TradeInMonthlyValue }}
                kr/mån eller
                {{ buybackStore.conditionDamaged?.TradeInMonthlyDataAmount }} GB
              </p>
            </BuybackCondition>
          </InputRadioGroup>
        </div>
      </div>
    </BuybackStep>
    <BuybackStep
      icon="gift"
      heading="Välj fördel"
      :active="buybackStore.step3Active"
    >
      <div v-if="buybackStore.step3Active">
        <div class="text-center py-16">
          <p
            v-if="buybackStore.selectedEstimate?.TradeInTotalValue !== 0"
            class="heading-xxs"
          >
            Preliminärt värde
          </p>
          <p
            v-if="buybackStore.selectedEstimate?.TradeInTotalValue !== 0"
            class="heading-xl"
          >
            {{ buybackStore.selectedEstimate?.TradeInTotalValue }} kr
          </p>
        </div>
        <div class="flex flex-col gap-16">
          <BuybackPerk v-model="showDrawer" perk-type="Data" />
          <BuybackPerk
            v-if="buybackStore.selectedEstimate?.TradeInTotalValue !== 0"
            v-model="showDrawer"
            perk-type="CostReduction"
          />
        </div>
      </div>
    </BuybackStep>

    <div class="mt-32">
      <h4 class="heading-xxs pb-24">Hur fungerar det?</h4>
      <div class="pb-24">
        <h5 class="heading-xxs">1. Värdera</h5>
        <p>
          Värdet beror på märke, modell och hur sliten din lur är. Informationen
          som behövs är modell, färg och minne på din gamla mobil, t ex iPhone
          XR, 128GB, svart och sedan din uppskattning av lurens skick. Med de
          uppgifterna får du en preliminär värdering tillsammans med ett
          erbjudande om sänkt månadskostnad eller mer surf när du förlänger ditt
          abonnemang i 24 månader tillsammans med en ny mobil
        </p>
      </div>
      <div>
        <h5 class="heading-xxs">2. Granskning och aktivering</h5>
        <p>
          När du får hem din nya mobil får du också med en returadresslapp för
          din gamla. Skicka in den i samma kartong som din nya till oss. Vi
          granskar din gamla lur, kontrollerar vilket skick den är i och ser om
          det överensstämmer med det du skrev i värderingen. Efter det
          återkommer vi till dig med en slutgiltig värdering och erbjudande. Är
          du nöjd med erbjudandet aktiveras det på ditt abonnemang, dvs du får
          antingen mer surf eller lägre månadskostnad. Är du inte nöjd efter
          granskningen och den slutgiltiga värderingen har du så klart rätt att
          få tillbaka din mobil.
        </p>
      </div>
    </div>
    <div
      v-if="buybackStore.showScrollIndicator"
      class="animate-bounce motion-reduce:animate-none bg-green-600 rounded-full text-text-primary-inverted sticky bottom-32 right-24 left-full mr-24 w-32 h-32 p-4 text-center"
    >
      <nuxt-icon name="arrow-down" class="text-[2.4rem]" aria-hidden="true" />
    </div>
  </AppDrawer>
</template>

<script lang="ts" setup>
import {
  PERFECT_CONDITION,
  DAMAGED_CONDITION,
  GOOD_CONDITION,
} from '~/models/ListingProduct.api.model';
import { onClickOutside } from '@vueuse/core';
import { useTemplateRef } from 'vue';

const target = useTemplateRef<HTMLElement>('target');

const showDrawer = ref(false);

const buybackStore = useBuybackStore();
buybackStore.getBuybackCampaignHero();

onClickOutside(target, () => {
  buybackStore.searchDropdownVisible = false;
});

interface Props {
  data: BuybackData;
  valuateOnlyHeading?: string;
  valuateOnlyIngress?: string;
  valuateOnlyText?: string;
  valuateOnlySteps?: {
    heading: string;
    text: string;
  }[];
}
const props = withDefaults(defineProps<Props>(), {
  valuateOnlyHeading: undefined,
  valuateOnlyIngress: undefined,
  valuateOnlyText: undefined,
  valuateOnlySteps: () => [],
});

buybackStore.$patch({
  data: props.data,
});
</script>

<style scoped></style>
